/**
 * Primary filtering operations for the restaurant list view.
 * This file contains the core filtering logic used by the main restaurant listing page.
 * It's optimized for real-time filtering as users interact with filter controls.
 * 
 * @important Do not consolidate with other filtering utilities!
 * This implementation is specifically tuned for performance in the main list view,
 * handling immediate user interactions and filter changes.
 */

import type { RestaurantWithEventSpaces } from "@/types/restaurant";
import { EVENT_TYPES } from "@/lib/constants";

export const filterByNeighborhood = (restaurants: RestaurantWithEventSpaces[], neighborhood: string) => {
  if (!neighborhood) return restaurants;
  return restaurants.filter(restaurant => 
    restaurant.neighborhood?.name === neighborhood
  );
};

export const filterBySearch = (restaurants: RestaurantWithEventSpaces[], search: string) => {
  if (!search) return restaurants;
  const searchLower = search.toLowerCase();
  return restaurants.filter(restaurant => 
    restaurant.name.toLowerCase().includes(searchLower) ||
    restaurant.description?.toLowerCase().includes(searchLower)
  );
};

export const filterByEventType = (restaurants: RestaurantWithEventSpaces[], eventType: string) => {
  // If eventType is empty, return all restaurants
  if (!eventType) return restaurants;
  
  // We only want to filter by valid event types
  if (!EVENT_TYPES.includes(eventType as any)) return restaurants;
  
  return restaurants.filter(restaurant => {
    return restaurant.eventspaces.some(space => {
      if (!space.event_types) return false;
      
      const spaceEventTypes = space.event_types
        .split(',')
        .map(type => type.trim());
      
      return spaceEventTypes.includes(eventType);
    });
  });
};

export const filterByGroupSize = (restaurants: RestaurantWithEventSpaces[], groupSize: string) => {
  if (!groupSize) return restaurants;

  const capacityRanges = {
    small: { min: 0, max: 20 },
    medium: { min: 20, max: 50 },
    large: { min: 50, max: Infinity }
  };

  const range = capacityRanges[groupSize as keyof typeof capacityRanges];
  if (!range) return restaurants;

  return restaurants.filter(restaurant => {
    return restaurant.eventspaces.some(space => {
      const capacity = parseInt(space.capacity || '0');
      return capacity >= range.min && capacity <= range.max;
    });
  });
};

export const filterByPriceRange = (restaurants: RestaurantWithEventSpaces[], priceRange: string) => {
  if (!priceRange) return restaurants;

  const priceRanges = {
    '1': { max: 1000 },
    '2': { max: 5000 },
    '3': { max: 10000 },
    '4': { max: Infinity }
  };

  const range = priceRanges[priceRange as keyof typeof priceRanges];
  if (!range) return restaurants;

  return restaurants.filter(restaurant => {
    return restaurant.eventspaces.some(space => {
      const maxSpend = Math.max(...(space.pricing_tiers || []).map(tier => tier.max_spend || 0));
      return maxSpend <= range.max;
    });
  });
};
